// 2026-09-30 addition — separate admin credential, separate token
// (localStorage "admin_token", distinct from the regular staff "token"),
// gates only the new /api/admin/* user-management endpoints.
import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { extractErrorDetail } from "./validators";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const B = { navyDeep: "#000e24", navy: "#00357a", gold: "#c9a96e" };

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = email.trim() !== "" && password !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/api/admin/login`, { email, password });
      localStorage.setItem("admin_token", res.data.access_token);
      // 2026-10-08 fix — real gap, reported directly: used to always
      // land on /admin regardless of where the admin was trying to go,
      // silently dropping deep links like a cross-app audit-log
      // click-through (?source=portal&identity=...). AdminRoute now
      // passes the original path+query via router state when it bounces
      // someone here; honor it if present, same fallback as before.
      navigate(location.state?.from || "/admin");
    } catch (err) {
      setError(extractErrorDetail(err, "Login failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      bgcolor: isDark ? "#080d1a" : "#f4f7ff", fontFamily: "'Inter', sans-serif",
    }}>
      {/* 2026-10-04 fix — real gap, reported directly: this page had no
          navbar at all, just the centered card — every other page in the
          app (Login.js included) carries the same real logo + top bar.
          Matches Login.js's navbar exactly (same bg/blur/gold-line/logo
          treatment) rather than inventing a different header here. */}
      <Box sx={{
        bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(0,31,77,0.96)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(201,169,110,0.2)",
      }}>
        <Box sx={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)" }} />
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 68,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
          <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 46, width: "auto" }} />
          <ThemeToggle />
        </Box>
      </Box>

      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 3, py: 6,
      }}>
      <Box sx={{
        width: 400, borderRadius: "20px", overflow: "hidden",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,53,122,0.1)"}`,
        boxShadow: "0 32px 80px rgba(0,31,77,0.14)",
        bgcolor: isDark ? "#111827" : "#fff",
      }}>
        <Box sx={{ height: 3, background: `linear-gradient(to right, ${B.navyDeep}, ${B.navy}, ${B.gold})` }} />
        <Box sx={{ px: 4, pt: 3.5, pb: 3, background: `linear-gradient(135deg, ${B.navyDeep} 0%, ${B.navy} 100%)` }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.2em", color: B.gold }}>
            ADMIN ACCESS
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#fff", mt: 0.5, fontFamily: "'Playfair Display', serif" }}>
            Labarium Admin
          </Typography>
        </Box>
        <Box sx={{ p: 4 }}>
          {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField label="Admin email" type="email" size="small" fullWidth required
              value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2.5 }} autoFocus />
            <TextField label="Password" type="password" size="small" fullWidth required
              value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mb: 3 }} />
            <Button type="submit" fullWidth variant="contained" disabled={loading || !canSubmit}
              sx={{ background: `linear-gradient(135deg, ${B.navyDeep}, ${B.navy})`, py: 1.4, fontWeight: 700, textTransform: "none" }}>
              {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Sign In as Admin"}
            </Button>
          </Box>
          <Typography sx={{ mt: 3, textAlign: "center" }}>
            <Link to="/login" style={{ fontSize: 14, color: isDark ? "#94a3b8" : "#64748b" }}>
              ← Back to regular sign in
            </Link>
          </Typography>
        </Box>
      </Box>
      </Box>
    </Box>
  );
}
